import { isManagerOrAbove } from '@/domain/permissions';
import { itemSections } from '@/domain/sheet';
import { EMPTY_MICROS, EMPTY_OPENINGS, SECTION_KIND, type AreaId, type BusinessDate, type CountEntry, type CountSource, type Item, type Kind, type CameFrom, type LossReason, type Micros, type MoveKind, type Movement, type Night, type Openings, type SectionId, type Settings } from '@/domain/types';
import { businessDateFor, formatClock, isBusinessDate, laterTime } from '@/domain/businessDate';
import { rowsOf } from './items';
import { rt } from './runtime';
import { getSettings } from './settings';
import { DomainError, inTx, newId, newMeta, nowIso, putAudited, touch, writeAudit } from './write';

export const AREA_LABEL: Record<AreaId, string> = { back_beer: 'Back beer', front_beer: 'Front bar beer', liquor: 'Liquor', guns: 'Liquor guns' };
export const SECTION_LABEL: Record<SectionId, string> = { back_beer: 'Back beer', front_beer: 'Front bar beer', liquor: 'Liquor', liqueur: 'Liqueurs' };
/** The lists counted in each area (the liquor area is both liquor lists). */
export const AREA_SECTIONS: Record<AreaId, SectionId[]> = { back_beer: ['back_beer'], front_beer: ['front_beer'], liquor: ['liquor', 'liqueur'], guns: [] };
export const areaOfSection = (s: SectionId): AreaId => (s === 'liqueur' ? 'liquor' : s);

export const countId = (date: BusinessDate, section: SectionId, itemId: string) => `${date}|${section}|${itemId}`;

export function blankNight(date: BusinessDate): Night {
  return { ...newMeta(date), id: date, date, status: 'counting', areasDone: {}, gun1: null, gun2: null, micros: { ...EMPTY_MICROS }, openings: { ...EMPTY_OPENINGS }, countInitials: '', recountInitials: '', finishedBy: null, finishedAt: null, approvedBy: null, approvedAt: null, okItems: {}, note: '' };
}

function assertDate(date: BusinessDate) {
  if (!isBusinessDate(date)) throw new DomainError(`“${date}” isn’t a date.`);
}

function requireManager(what: string) {
  if (!isManagerOrAbove(rt().actor.role)) throw new DomainError(`Only a manager can ${what}.`);
}

async function ensureNight(date: BusinessDate): Promise<Night> {
  assertDate(date);
  const db = rt().db;
  const n = await db.nights.get(date);
  if (n) return n;
  const fresh = blankNight(date);
  await db.nights.put(fresh);
  return fresh;
}

/** When recounts of a night close, the day after it ("HH:mm"; never before the night ends). */
export const recountTime = (s: Pick<Settings, 'cutoff' | 'recountUntil'>) => laterTime(s.cutoff, s.recountUntil ?? s.cutoff);

/**
 * The oldest night that can still be recounted. A recount only means something while the
 * stock is as it was at close, so it's open until the recount time the next day (4 p.m. at
 * Red's). Later, staff would count what's on the shelf now and it would be saved as that
 * night's count (and carried into the next night's opening).
 */
export async function recountDay(): Promise<BusinessDate> {
  const s = await getSettings();
  return businessDateFor(new Date(), s.timezone, recountTime(s));
}

/** `oldest` is `recountDay()` (or `useRecountDay()` on a screen). */
export const recountStillPossible = (date: BusinessDate, oldest: BusinessDate) => date >= oldest;

export const tooLateToRecount = (s: Pick<Settings, 'cutoff' | 'recountUntil'>) =>
  `Too late to recount: recounts can be asked for until ${formatClock(recountTime(s))} the day after the count, and the stock has changed since. Fix the number yourself, or tap “Looks right”.`;

async function myInitials(): Promise<string> {
  const p = await rt().db.people.get(rt().actor.userId);
  return p?.initials ?? rt().actor.name.slice(0, 2).toUpperCase();
}

/** Validates an amount for a sheet: beer in whole bottles, liquor in ounces (up to 2 decimals). */
export function checkAmount(kind: Kind, v: number, what = 'The count'): void {
  if (!Number.isFinite(v) || v < 0) throw new DomainError(`${what} can’t be below 0.`);
  if (kind === 'beer' && !Number.isInteger(v)) throw new DomainError(`${what} is whole bottles or cans for beer.`);
  if (kind === 'liquor' && Math.round(v * 100) !== Math.round(v * 1e6) / 1e4) throw new DomainError(`${what} has too many decimals (2 at most, like 23.65).`);
  if (v > 10000) throw new DomainError(`${what} looks too big. Check the number.`);
}

/**
 * Who can change a count:
 *  - nobody while the night is approved (a manager reopens it first);
 *  - staff while their area isn't finished, and any drink a manager asked to be recounted;
 *  - managers any time before approval.
 * Returns the reason it can't be changed, or null.
 */
export function editBlockReason(night: Night | undefined, area: AreaId, entry: CountEntry | undefined, role = rt().actor.role, oldestRecount?: BusinessDate): string | null {
  if (night?.status === 'approved') return 'This night is approved. A manager has to reopen it to change anything.';
  if (isManagerOrAbove(role)) return null;
  if (entry?.recountRequestedAt && (oldestRecount == null || recountStillPossible(entry.date, oldestRecount))) return null;
  if (night?.status === 'finished') return 'Tonight’s count is finished. Ask a manager if something needs changing.';
  if (night?.areasDone[area]) return 'This area is marked done. Tap “Change something” to open it again.';
  return null;
}

export async function setCount(date: BusinessDate, section: SectionId, itemId: string, value: number | null, source: CountSource = 'typed'): Promise<void> {
  const kind = SECTION_KIND[section];
  if (value != null) checkAmount(kind, value);
  const oldestRecount = await recountDay();
  await inTx(['nights', 'counts', 'items'], async () => {
    const db = rt().db;
    const item = await db.items.get(itemId);
    if (!item) throw new DomainError('That drink no longer exists.');
    if (item.blankRow) throw new DomainError('That row is kept blank. A manager can turn it into a drink in Drink lists.');
    const night = await ensureNight(date);
    const id = countId(date, section, itemId);
    const before = await db.counts.get(id);
    const blocked = editBlockReason(night, areaOfSection(section), before, rt().actor.role, oldestRecount);
    if (blocked) throw new DomainError(blocked);
    if (before && before.value === value) return;
    const next: CountEntry = before
      ? touch(before, { value, countedBy: rt().actor.userId, countedAt: nowIso(), source: before.recountRequestedAt ? 'recount' : source })
      : { ...newMeta(id), id, date, section, itemId, value, countedBy: rt().actor.userId, countedAt: nowIso(), source, recountRequestedAt: null, recountRequestedBy: null, recountNote: '' };
    await putAudited('counts', next, before, { action: 'count.set', summary: `${date} · ${item.name} (${SECTION_LABEL[section].toLowerCase()}): ${before?.value != null ? `${before.value} → ` : ''}${value ?? 'cleared'}` });
  });
}

/**
 * Front bar beer: fills every drink that hasn't been counted with its usual amount, in one
 * tap. Drinks a manager set to "count it every time" are left alone. Returns how many.
 */
export async function fillUsualAmounts(date: BusinessDate): Promise<number> {
  const oldestRecount = await recountDay();
  return inTx(['nights', 'counts', 'items'], async () => {
    const db = rt().db;
    const night = await ensureNight(date);
    let filled = 0;
    for (const item of rowsOf(await db.items.toArray(), 'front_beer')) {
      if (item.usualFront == null) continue;
      const id = countId(date, 'front_beer', item.id);
      const before = await db.counts.get(id);
      if (before?.value != null) continue;
      const blocked = editBlockReason(night, 'front_beer', before, rt().actor.role, oldestRecount);
      if (blocked) throw new DomainError(blocked);
      const next: CountEntry = before
        ? touch(before, { value: item.usualFront, countedBy: rt().actor.userId, countedAt: nowIso(), source: 'usual' })
        : { ...newMeta(id), id, date, section: 'front_beer', itemId: item.id, value: item.usualFront, countedBy: rt().actor.userId, countedAt: nowIso(), source: 'usual', recountRequestedAt: null, recountRequestedBy: null, recountNote: '' };
      await db.counts.put(next);
      filled++;
    }
    if (filled) await writeAudit({ action: 'count.fill_usual', entity: 'nights', entityId: date, summary: `Filled in the usual amount for ${filled} front bar beers` });
    return filled;
  });
}

/** Rows on an area's lists that have no number yet. */
export async function uncountedIn(date: BusinessDate, area: AreaId): Promise<Item[]> {
  const db = rt().db;
  const items = await db.items.toArray();
  const counts = new Map((await db.counts.where('date').equals(date).toArray()).map((c) => [c.id, c]));
  const out: Item[] = [];
  for (const s of AREA_SECTIONS[area]) for (const it of rowsOf(items, s)) if (counts.get(countId(date, s, it.id))?.value == null) out.push(it);
  return out;
}

export async function setAreaDone(date: BusinessDate, area: AreaId, done: boolean): Promise<void> {
  await inTx(['nights', 'counts', 'items'], async () => {
    const night = await ensureNight(date);
    if (night.status === 'approved') throw new DomainError('This night is approved. A manager has to reopen it first.');
    if (night.status === 'finished' && !isManagerOrAbove(rt().actor.role)) throw new DomainError('Tonight’s count is finished. Ask a manager if something needs changing.');
    if (done) {
      if (area === 'guns') {
        if (night.gun1 == null || night.gun2 == null) throw new DomainError('Type both gun readings first.');
      } else {
        const missing = await uncountedIn(date, area);
        if (missing.length) throw new DomainError(`${missing.length} drink${missing.length === 1 ? ' still has' : 's still have'} no number (${missing.slice(0, 3).map((m) => m.name).join(', ')}${missing.length > 3 ? '…' : ''}). If there are none, enter 0.`);
      }
    }
    const areasDone = { ...night.areasDone };
    if (done) areasDone[area] = { by: rt().actor.userId, at: nowIso() };
    else delete areasDone[area];
    await putAudited('nights', touch(night, { areasDone }), night, { action: done ? 'area.done' : 'area.reopen', summary: `${AREA_LABEL[area]} ${done ? 'done' : 'opened again'}` });
  });
}

export async function setGuns(date: BusinessDate, gun1: number | null, gun2: number | null): Promise<void> {
  for (const [label, v] of [['Gun 1', gun1], ['Gun 2', gun2]] as const) {
    if (v == null) continue;
    if (!Number.isFinite(v) || v < 0) throw new DomainError(`${label} can’t be below 0.`);
    if (v > 1e9) throw new DomainError(`${label} looks too big. Check the number.`);
  }
  await inTx(['nights'], async () => {
    const night = await ensureNight(date);
    const blocked = editBlockReason(night, 'guns', undefined);
    if (blocked) throw new DomainError(blocked);
    if (night.gun1 === gun1 && night.gun2 === gun2) return;
    await putAudited('nights', touch(night, { gun1, gun2 }), night, { action: 'guns.set', summary: `Gun readings: ${gun1 ?? '—'} / ${gun2 ?? '—'}` });
  });
}

export const MOVE_LABEL: Record<MoveKind, string> = { added: 'Came in', transfer_out: 'Moved out', promo_spill: 'Promo or spilled' };
export const LOSS_LABEL: Record<LossReason, string> = { promo: 'Promo / given away', spilled: 'Spilled', broken: 'Broken', other: 'Something else' };
export const FROM_LABEL: Record<CameFrom, string> = { downstairs: 'Brought from downstairs', delivery: 'From a delivery' };
/** "Spilled", "Brought from downstairs", "Came in"… — the words for one entry. */
export const moveLabel = (m: Pick<Movement, 'kind' | 'reason' | 'from'>) => (m.kind === 'promo_spill' && m.reason ? LOSS_LABEL[m.reason] : m.kind === 'added' && m.from ? FROM_LABEL[m.from] : MOVE_LABEL[m.kind]);

export async function addMove(input: { date: BusinessDate; kind: MoveKind; itemId: string; qty: number; note?: string; reason?: LossReason; from?: CameFrom; broughtBy?: string; bottles?: number; bottleOz?: number }): Promise<Movement> {
  return inTx(['nights', 'moves', 'items', 'people'], async () => {
    const db = rt().db;
    const item = await db.items.get(input.itemId);
    if (!item) throw new DomainError('Pick a drink.');
    if (item.kind === 'liquor' && input.kind === 'transfer_out') throw new DomainError('The liquor sheet has no “moved out” row. Use “Promo or spilled”, or ask a manager.');
    if (!(input.qty > 0)) throw new DomainError('Type how many (more than 0).');
    checkAmount(item.kind, input.qty, 'The amount');
    if (input.reason && input.kind !== 'promo_spill') throw new DomainError('Only promo or spillage has a “what happened”.');
    if (input.reason === 'other' && !(input.note ?? '').trim()) throw new DomainError('Say what happened in the note.');
    if (input.from && input.kind !== 'added') throw new DomainError('Only what came in has a “from”.');
    if (input.bottles != null && (!Number.isInteger(input.bottles) || input.bottles <= 0 || item.kind !== 'liquor')) throw new DomainError('Bottles are a whole number, for liquor.');
    if (input.broughtBy && input.kind !== 'added') throw new DomainError('Only what came in has a “brought up by”.');
    const carrier = input.broughtBy && input.broughtBy !== rt().actor.userId ? await db.people.get(input.broughtBy) : null;
    if (input.broughtBy && input.broughtBy !== rt().actor.userId && !carrier) throw new DomainError('Pick who brought it up from the list.');
    const night = await ensureNight(input.date);
    if (night.status === 'approved') throw new DomainError('This night is approved. A manager has to reopen it first.');
    if (night.status === 'finished' && !isManagerOrAbove(rt().actor.role)) throw new DomainError('Tonight’s count is finished. Ask a manager to add this.');
    const m: Movement = { ...newMeta(newId('mov-')), date: input.date, kind: input.kind, sheet: item.kind, itemId: item.id, qty: input.qty, ...(input.reason ? { reason: input.reason } : {}), ...(input.from ? { from: input.from } : {}), ...(carrier ? { broughtBy: carrier.id } : {}), ...(input.bottles != null && input.bottleOz != null ? { bottles: input.bottles, bottleOz: input.bottleOz } : {}), note: (input.note ?? '').trim(), by: rt().actor.userId, at: nowIso(), voided: false, voidedBy: null, voidedAt: null };
    await putAudited('moves', m, null, { action: 'move.add', summary: `${m.date} · ${moveLabel(m)}: ${item.name} ${m.qty}${item.kind === 'liquor' ? ' oz' : ''}${carrier ? `, brought up by ${carrier.name}` : ''}${m.note ? ` (${m.note})` : ''}` });
    return m;
  });
}

export async function voidMove(id: string): Promise<void> {
  await inTx(['nights', 'moves'], async () => {
    const db = rt().db;
    const before = await db.moves.get(id);
    if (!before || before.voided) return;
    const night = await db.nights.get(before.date);
    if (night?.status === 'approved') throw new DomainError('This night is approved. A manager has to reopen it first.');
    if (night?.status === 'finished' && !isManagerOrAbove(rt().actor.role)) throw new DomainError('Tonight’s count is finished. Ask a manager to change this.');
    if (!isManagerOrAbove(rt().actor.role) && before.by !== rt().actor.userId) throw new DomainError('Only the person who added it, or a manager, can take it off.');
    await putAudited('moves', touch(before, { voided: true, voidedBy: rt().actor.userId, voidedAt: nowIso() }), before, { action: 'move.void', summary: `${before.date} · Took off a “${moveLabel(before).toLowerCase()}” entry (${before.qty})` });
  });
}

/** Staff finished every area: the night goes to a manager. */
export async function finishNight(date: BusinessDate): Promise<void> {
  await inTx(['nights', 'people'], async () => {
    const db = rt().db;
    const night = await ensureNight(date);
    if (night.status !== 'counting') return;
    const notDone = (['back_beer', 'front_beer', 'liquor', 'guns'] as AreaId[]).filter((a) => !night.areasDone[a]);
    if (notDone.length) throw new DomainError(`Not done yet: ${notDone.map((a) => AREA_LABEL[a]).join(', ')}.`);
    const ids = [...new Set(Object.values(night.areasDone).map((d) => d!.by))];
    const initials = (await Promise.all(ids.map((id) => db.people.get(id)))).map((p) => p?.initials).filter(Boolean);
    const next = touch(night, { status: 'finished', finishedBy: rt().actor.userId, finishedAt: nowIso(), countInitials: [...new Set(initials)].join('/') });
    await putAudited('nights', next, night, { action: 'night.finish', summary: 'Finished the count' });
  });
}

export async function setMicros(date: BusinessDate, micros: Micros): Promise<void> {
  requireManager('type the Micros numbers');
  for (const [k, v] of Object.entries(micros)) if (v != null && (!Number.isFinite(v) || v < 0 || !Number.isInteger(v))) throw new DomainError(`${k}: Micros numbers are whole numbers of 0 or more.`);
  await inTx(['nights'], async () => {
    const night = await ensureNight(date);
    if (night.status === 'approved') throw new DomainError('This night is approved. Reopen it to change the Micros numbers.');
    await putAudited('nights', touch(night, { micros: { ...EMPTY_MICROS, ...micros } }), night, { action: 'night.micros', summary: 'Typed the Micros numbers' });
  });
}

export async function setOpenings(date: BusinessDate, openings: Openings): Promise<void> {
  requireManager('change opening numbers');
  for (const v of Object.values(openings)) if (v != null && (!Number.isFinite(v) || v < 0)) throw new DomainError('Opening numbers can’t be below 0.');
  await inTx(['nights'], async () => {
    const night = await ensureNight(date);
    if (night.status === 'approved') throw new DomainError('This night is approved. Reopen it first.');
    await putAudited('nights', touch(night, { openings: { ...EMPTY_OPENINGS, ...openings } }), night, { action: 'night.openings', summary: 'Changed the opening numbers' });
  });
}

export async function setNightNote(date: BusinessDate, note: string): Promise<void> {
  await inTx(['nights'], async () => {
    const night = await ensureNight(date);
    if (night.note === note) return;
    await putAudited('nights', touch(night, { note: note.slice(0, 1000) }), night, { action: 'night.note', summary: 'Changed the note' });
  });
}

/**
 * A manager asks for a drink to be counted again. It shows on the staff home screen and
 * stays there until someone taps "Done — I counted it again".
 */
export async function requestRecount(date: BusinessDate, itemId: string, note = ''): Promise<void> {
  requireManager('ask for a recount');
  if (!recountStillPossible(date, await recountDay())) throw new DomainError(tooLateToRecount(await getSettings()));
  await inTx(['nights', 'counts', 'items'], async () => {
    const db = rt().db;
    const item = await db.items.get(itemId);
    if (!item) throw new DomainError('That drink no longer exists.');
    const night = await ensureNight(date);
    if (night.status === 'approved') throw new DomainError('This night is approved. Reopen it first.');
    const at = nowIso();
    for (const s of itemSections(item)) {
      const id = countId(date, s, itemId);
      const before = await db.counts.get(id);
      const next: CountEntry = before
        ? touch(before, { recountRequestedAt: at, recountRequestedBy: rt().actor.userId, recountNote: note.trim() })
        : { ...newMeta(id), id, date, section: s, itemId, value: null, countedBy: null, countedAt: null, source: 'typed', recountRequestedAt: at, recountRequestedBy: rt().actor.userId, recountNote: note.trim() };
      await db.counts.put(next);
    }
    const okItems = { ...night.okItems };
    delete okItems[itemId];
    await putAudited('nights', touch(night, { okItems }), night, { action: 'recount.request', summary: `Asked for ${item.name} to be counted again`, reason: note });
  });
}

/**
 * Someone counted it again: clears the request (on the given lists, or all of them) and adds
 * their initials to "Recount Initial".
 */
export async function completeRecount(date: BusinessDate, itemId: string, sections?: SectionId[]): Promise<void> {
  const initials = await myInitials();
  const late = !recountStillPossible(date, await recountDay());
  await inTx(['nights', 'counts', 'items'], async () => {
    const db = rt().db;
    const entries = (await db.counts.where('date').equals(date).toArray()).filter((c) => c.itemId === itemId && c.recountRequestedAt && (!sections || sections.includes(c.section)));
    if (entries.length === 0) return;
    if (late) throw new DomainError('Too late: the time for recounts on that night has passed, and the stock has changed since. A manager will sort it out.');
    if (entries.some((e) => e.value == null)) throw new DomainError('Type a number first (0 if there are none).');
    for (const e of entries) await db.counts.put(touch(e, { recountRequestedAt: null, recountRequestedBy: null, recountNote: '' }));
    const night = await ensureNight(date);
    const have = night.recountInitials.split('/').filter(Boolean);
    const recountInitials = have.includes(initials) ? night.recountInitials : [...have, initials].join('/');
    const item = await db.items.get(itemId);
    await putAudited('nights', touch(night, { recountInitials }), night, { action: 'recount.done', summary: `Counted ${item?.name ?? 'a drink'} again` });
  });
}

export async function cancelRecount(date: BusinessDate, itemId: string): Promise<void> {
  requireManager('cancel a recount');
  await inTx(['counts'], async () => {
    const db = rt().db;
    for (const e of (await db.counts.where('date').equals(date).toArray()).filter((c) => c.itemId === itemId && c.recountRequestedAt)) {
      await putAudited('counts', touch(e, { recountRequestedAt: null, recountRequestedBy: null, recountNote: '' }), e, { action: 'recount.cancel', summary: 'Cancelled a recount' });
    }
  });
}

/** A manager looked at a flagged drink and says it's right (with the numbers they saw). */
export async function markOk(date: BusinessDate, itemId: string, seen: { used: number | null; close: number | null }, note = ''): Promise<void> {
  requireManager('approve a count');
  await inTx(['nights'], async () => {
    const night = await ensureNight(date);
    if (night.status === 'approved') return;
    const okItems = { ...night.okItems, [itemId]: { by: rt().actor.userId, at: nowIso(), note: note.trim(), used: seen.used, close: seen.close } };
    await putAudited('nights', touch(night, { okItems }), night, { action: 'count.ok', summary: 'Said a count looks right', reason: note });
  });
}

export async function approveNight(date: BusinessDate, leftOpen: { itemId: string; used: number | null; close: number | null }[]): Promise<void> {
  requireManager('approve a night');
  const late = !recountStillPossible(date, await recountDay());
  await inTx(['nights', 'counts'], async () => {
    const db = rt().db;
    const night = await ensureNight(date);
    if (night.status === 'approved') return;
    const waiting = (await db.counts.where('date').equals(date).toArray()).filter((c) => c.recountRequestedAt);
    if (waiting.length && !late) throw new DomainError('A recount is still waiting. Wait for it, or cancel it, then approve.');
    // Recounts nobody did before the night ended can't be done any more: the counts stay as they were.
    for (const e of waiting) await db.counts.put(touch(e, { recountRequestedAt: null, recountRequestedBy: null, recountNote: '' }));
    const missed = new Set(waiting.map((e) => e.itemId)).size;
    const okItems = { ...night.okItems };
    const at = nowIso();
    for (const f of leftOpen) okItems[f.itemId] ??= { by: rt().actor.userId, at, note: 'Approved with the night', used: f.used, close: f.close };
    const next = touch(night, { status: 'approved', approvedBy: rt().actor.userId, approvedAt: at, okItems });
    await putAudited('nights', next, night, { action: 'night.approve', summary: missed ? `Approved the night (${missed} recount${missed === 1 ? '' : 's'} not done in time)` : 'Approved the night' });
  });
}

export async function reopenNight(date: BusinessDate, reason: string): Promise<void> {
  requireManager('reopen a night');
  if (reason.trim().length < 3) throw new DomainError('Say why it’s being reopened.');
  await inTx(['nights'], async () => {
    const night = await ensureNight(date);
    if (night.status === 'counting') return;
    await putAudited('nights', touch(night, { status: 'finished', approvedBy: null, approvedAt: null }), night, { action: 'night.reopen', summary: 'Reopened the night', reason });
  });
}

/** Staff side: open a finished night again for more counting (manager only). */
export async function backToCounting(date: BusinessDate): Promise<void> {
  requireManager('open the count again');
  if (!recountStillPossible(date, await recountDay())) throw new DomainError(`Too late to send it back: recounts close at ${formatClock(recountTime(await getSettings()))} the day after the count, and the stock has changed since. Fix the numbers yourself in the count.`);
  await inTx(['nights'], async () => {
    const night = await ensureNight(date);
    if (night.status !== 'finished') return;
    await putAudited('nights', touch(night, { status: 'counting', finishedBy: null, finishedAt: null }), night, { action: 'night.recount_all', summary: 'Opened the count again for staff' });
  });
}
